import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { getDueRecurringExpenses } from '../services/financials/recurring'
import type { DueRecurringExpense } from '../types/financial'

interface DueRecurringExpensesContextValue {
  dueExpenses: DueRecurringExpense[]
  dueCount: number
  isLoading: boolean
  refetch: () => void
}

const DueRecurringExpensesContext = createContext<DueRecurringExpensesContextValue>({
  dueExpenses: [],
  dueCount: 0,
  isLoading: true,
  refetch: () => {},
})

export function DueRecurringExpensesProvider({ children }: { children: React.ReactNode }) {
  const [dueExpenses, setDueExpenses] = useState<DueRecurringExpense[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const location = useLocation()

  const fetchDue = useCallback(async () => {
    try {
      const data = await getDueRecurringExpenses()
      setDueExpenses(data)
    } catch {
      // Non-blocking — badges and banner will show 0
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => { fetchDue() }, [fetchDue, location.pathname])

  return (
    <DueRecurringExpensesContext.Provider value={{
      dueExpenses,
      dueCount: dueExpenses.length,
      isLoading,
      refetch: fetchDue,
    }}>
      {children}
    </DueRecurringExpensesContext.Provider>
  )
}

export function useDueRecurringExpenses() {
  return useContext(DueRecurringExpensesContext)
}
